import { gzip } from 'pako';
import { sendRequest } from '../../utils/network';
import { SentryReporterService } from '../sentryReporter';

export type NewRelicEvent<T> = Record<string, string | number | boolean | null> & {
    eventType: T;
};

export interface NewRelicReporterServiceOptions {
    apiEndpoint: string;
    apiInsertKey: string;
    getTimestamp?: () => number;
    batchedSendIntervalDuration?: number;
    batchedSendAmount?: number;
    sessionId?: string;
    sentryReporterService?: SentryReporterService;
}

export interface NewRelicReporterService<T> {
    sendEvents: (events: NewRelicEvent<T>[]) => void;
    flushEventsQueue: () => void;
}

export const createNewRelicReporterService = <T>({
    apiEndpoint,
    apiInsertKey,
    getTimestamp = () => new Date().getTime(),
    batchedSendIntervalDuration = 10 * 60 * 1000,
    batchedSendAmount = 50,
    sessionId,
    sentryReporterService,
}: NewRelicReporterServiceOptions): NewRelicReporterService<T> => {
    let batchedEvents: NewRelicEvent<T>[] = [];

    const sendBatchedEvents = async () => {
        if (batchedEvents.length === 0) {
            return;
        }

        const compressedEvents = gzip(JSON.stringify(batchedEvents));
        batchedEvents = [];

        await sendRequest(
            apiEndpoint,
            {
                accept: '*/*',
                customHeaders: {
                    'X-Insert-Key': apiInsertKey,
                    'Content-Type': 'application/json',
                    'Content-Encoding': 'gzip',
                },
                stringifyData: false,
            },
            compressedEvents
        );
    };

    const sendBatchedEventsHandled = () => {
        sendBatchedEvents()
            .then(() => {})
            .catch((error) => {
                sentryReporterService?.logWarning({ error, extra: { origin: 'newRelicReporterService' } });
            });
    };

    const sendEvents = (events: NewRelicEvent<T>[]) => {
        const timestamp = Math.round(getTimestamp());

        const enhancedEvents = events.map(
            (event) =>
                ({
                    sessionId: sessionId ?? null,
                    timestamp,
                    ...event,
                }) as unknown as NewRelicEvent<T>
        );

        batchedEvents = [...batchedEvents, ...enhancedEvents];

        if (batchedEvents.length >= batchedSendAmount) {
            sendBatchedEventsHandled();
        }
    };

    window.setInterval(sendBatchedEventsHandled, batchedSendIntervalDuration);

    return {
        sendEvents,
        flushEventsQueue: sendBatchedEventsHandled,
    };
};

export const createFakeNewRelicReporterService = <T>(): NewRelicReporterService<T> => ({
    sendEvents: () => {},
    flushEventsQueue: () => {},
});
